import { Fragment, type ReactNode } from "react";

/**
 * Deliberately generic, single-pass tokenizer shared by all dump languages
 * (C++, JSON, C#). Not a real grammar — just enough lexical coloring to look
 * sharp against the dark theme. Hex offsets get their own class because they're
 * the actual payload. Rules are tried in order at each position.
 */
const RULES: ReadonlyArray<[type: string, re: RegExp]> = [
    ["comment", /^\/\/[^\n]*/],
    ["comment", /^\/\*[\s\S]*?\*\//],
    ["preproc", /^#[a-zA-Z]+/],
    ["string", /^"(?:\\.|[^"\\])*"/],
    ["string", /^'(?:\\.|[^'\\])*'/],
    ["string", /^<[A-Za-z0-9_./]+>/],
    ["hex", /^0x[0-9a-fA-F]+/],
    ["number", /^\d+(?:\.\d+)?/],
    [
        "keyword",
        /^(?:namespace|constexpr|const|static|public|private|class|struct|void|int32_t|uint32_t|uint8_t|uint64_t|int|float|double|bool|char|nint|std|ptrdiff_t|return|using|include|pragma|true|false|null)\b/,
    ],
    ["ident", /^[A-Za-z_]\w*/],
    ["ws", /^\s+/],
    ["punct", /^[^\s]/],
];

function tokenizeLine(line: string, lineKey: number): ReactNode {
    if (line === "") return null;

    const nodes: ReactNode[] = [];
    let rest = line;
    let i = 0;

    while (rest.length > 0) {
        let matched = false;

        for (const [type, re] of RULES) {
            const m = re.exec(rest);
            if (!m) continue;

            const value = m[0];
            if (type === "ws") {
                nodes.push(<Fragment key={i}>{value}</Fragment>);
            } else {
                nodes.push(
                    <span key={i} className={`tok-${type}`}>
                        {value}
                    </span>,
                );
            }
            rest = rest.slice(value.length);
            i += 1;
            matched = true;
            break;
        }

        // Safety valve: never loop forever on an unmatched char.
        if (!matched) {
            nodes.push(<Fragment key={i}>{rest[0]}</Fragment>);
            rest = rest.slice(1);
            i += 1;
        }
    }

    return <Fragment key={lineKey}>{nodes}</Fragment>;
}

export function CodeViewer({ code }: { code: string }) {
    // Drop a single trailing newline so we don't render an empty final line.
    const lines = code.replace(/\n$/, "").split("\n");

    return (
        <pre className="cv">
            <code className="cv-code">
                {lines.map((line, idx) => (
                    <span className="cv-line" key={idx}>
                        <span className="cv-ln" aria-hidden="true">
                            {idx + 1}
                        </span>
                        <span className="cv-text">
                            {tokenizeLine(line, idx)}
                        </span>
                    </span>
                ))}
            </code>
        </pre>
    );
}
